import type { ReactElement, ReactNode } from "react"
import { memo } from "react"
import ParallaxWrapperOrg from "@onestore/hel/dist/components/organisms/ParallaxWrapperOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import { useCheckoutConfigurationQuery } from "@gatsby-plugin-checkout/hooks/useCheckoutConfigurationQuery"
import Header from "@gatsby-plugin-onestore/components/Header"
import HeaderWithSteps from "@gatsby-plugin-onestore/components/HeaderWithSteps"
import { useLocation } from "@reach/router"
import Cookie from "~/components/Cookie"
import ErrorBoundary from "~/components/ErrorBoundary"
import FooterTemplate from "~/components/FooterTemplate"
import ModalHandler from "~/components/ModalHandler"
import Notification from "~/components/Notification"
import NotificationBar from "~/components/NotificationBar"
import { HeaderContextProvider } from "~/context/HeaderContext"
import { PriceTypeContextProvider } from "~/context/PriceTypeContext"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"
import UserdataContext from "../context/UserdataContext"

type BaseProps = {
  children: ReactNode
  pageContext?: {
    isBasket?: boolean
    isBonus?: boolean
    hasDarkHeader?: boolean
    hasHeaderHideBoxShadow?: boolean
    hasHiddenFooter?: boolean
    isMarketplace?: boolean
    isPriceListPage?: boolean
  }
  hasParallaxWrapper?: boolean
}

const BONUS_PATH_SEGMENT = "/bonus"
const CHECKOUT_PATH_SEGMENTS = ["/koszyk", "/zamowienie"]

function Base({
  children,
  pageContext,
  hasParallaxWrapper = false,
}: BaseProps): ReactElement<BaseProps> {
  const getLocationType = (
    path: string
  ): "bonus" | "checkout" | "productPage" => {
    if (path.includes(BONUS_PATH_SEGMENT)) return "bonus"
    if (CHECKOUT_PATH_SEGMENTS.some((segment) => path.includes(segment)))
      return "checkout"
    return "productPage"
  }

  const { hasStepsInHeader } = useCheckoutConfigurationQuery()
  const { showPriceTypeSwitcher } = usePriceConfigurationQuery()

  const isBasket = pageContext?.isBasket || false
  const isBonus = pageContext?.isBonus || false
  const hasDarkHeader = pageContext?.hasDarkHeader || false
  const hasHeaderHideBoxShadow = pageContext?.hasHeaderHideBoxShadow || false
  const hasHiddenFooter = pageContext?.hasHiddenFooter || false
  const isMarketplace = pageContext?.isMarketplace || false
  const isPriceListPage = pageContext?.isPriceListPage || false
  const hasPriceTypeSwitcher = showPriceTypeSwitcher && !isBasket

  const hasSimpleHeaderWithSteps = hasStepsInHeader && (isBonus || isBasket)

  const { pathname } = useLocation() as Window["location"]
  const location = getLocationType(pathname)

  return (
    <ErrorBoundary>
      <ConditionalWrapper
        condition={hasParallaxWrapper}
        wrapper={(children) => (
          <ParallaxWrapperOrg hasFullHeight>{children}</ParallaxWrapperOrg>
        )}
      >
        <UserdataContext>
          <div id="pageNotificationBars">
            <Notification />

            <NotificationBar location={location} />
          </div>

          <div className="ias-zone" data-ias-zoneid="penguin" />

          <HeaderContextProvider>
            <PriceTypeContextProvider
              isMarketplace={isMarketplace}
              isPriceListPage={isPriceListPage}
            >
              {hasSimpleHeaderWithSteps ? (
                <HeaderWithSteps
                  isBonusPage={isBonus}
                  isBasketPage={isBasket}
                  hasPriceTypeSwitcher={hasPriceTypeSwitcher}
                />
              ) : (
                <Header
                  disableCartClick={isBasket}
                  isDarkMode={hasDarkHeader}
                  hasHideBoxShadow={hasHeaderHideBoxShadow}
                  hasPriceTypeSwitcher={hasPriceTypeSwitcher}
                />
              )}

              <main>{children}</main>
            </PriceTypeContextProvider>
          </HeaderContextProvider>

          <ModalHandler />

          <Cookie />

          {!hasHiddenFooter ? (
            <FooterTemplate hideMobileFooter={isBasket || isBonus} />
          ) : null}
        </UserdataContext>
      </ConditionalWrapper>
    </ErrorBoundary>
  )
}

export default memo(Base)
